import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Boxes, Plus, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { useIsSuperadmin } from "@/hooks/useUserRole";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import SparePartBaseLogicSection from "@/components/admin/SparePartBaseLogicSection";
import { SettingsBackLink } from "@/components/settings/SettingsBackLink";

interface ApplianceCategory {
  id: string;
  name: string;
  created_at: string;
}

export default function ApplianceCategoryManagement() {
  const { isSuperadmin, isLoading } = useIsSuperadmin();
  const navigate = useNavigate();

  const [categories, setCategories] = useState<ApplianceCategory[]>([]);
  const [newCategoryName, setNewCategoryName] = useState("");
  const [editCategory, setEditCategory] = useState<ApplianceCategory | null>(null);
  const [editCategoryName, setEditCategoryName] = useState("");
  const [deleteCategory, setDeleteCategory] = useState<ApplianceCategory | null>(null);
  const [busy, setBusy] = useState(false);

  const loadCategories = async () => {
    const { data, error } = await supabase
      .from("appliance_categories")
      .select("id, name, created_at")
      .order("name");
    if (error) {
      console.warn("[appliance_categories] load failed", error);
      return;
    }
    setCategories((data as ApplianceCategory[]) || []);
  };

  useEffect(() => {
    if (isSuperadmin) loadCategories();
  }, [isSuperadmin]);

  const handleCreateCategory = async () => {
    const trimmed = newCategoryName.trim();
    if (!trimmed) return;
    if (categories.some((c) => c.name.toLowerCase() === trimmed.toLowerCase())) {
      toast.error(`"${trimmed}" already exists`);
      return;
    }
    setBusy(true);
    try {
      const { error } = await supabase.from("appliance_categories").insert({ name: trimmed });
      if (error) throw error;
      toast.success(`Category "${trimmed}" added`);
      setNewCategoryName("");
      await loadCategories();
    } catch (err: any) {
      toast.error(err.message ?? "Failed to add category");
    } finally {
      setBusy(false);
    }
  };

  const handleUpdateCategory = async () => {
    if (!editCategory) return;
    const trimmed = editCategoryName.trim();
    if (!trimmed) return;
    if (trimmed === editCategory.name) {
      setEditCategory(null);
      return;
    }
    if (categories.some((c) => c.id !== editCategory.id && c.name.toLowerCase() === trimmed.toLowerCase())) {
      toast.error(`"${trimmed}" already exists`);
      return;
    }
    setBusy(true);
    try {
      const { error } = await supabase
        .from("appliance_categories")
        .update({ name: trimmed })
        .eq("id", editCategory.id);
      if (error) throw error;
      toast.success("Category updated");
      setEditCategory(null);
      setEditCategoryName("");
      await loadCategories();
    } catch (err: any) {
      toast.error(err.message ?? "Failed to update category");
    } finally {
      setBusy(false);
    }
  };

  const handleDeleteCategory = async () => {
    if (!deleteCategory) return;
    setBusy(true);
    try {
      const { error } = await supabase
        .from("appliance_categories")
        .delete()
        .eq("id", deleteCategory.id);
      if (error) throw error;
      toast.success(`Deleted "${deleteCategory.name}"`);
      setDeleteCategory(null);
      await loadCategories();
    } catch (err: any) {
      toast.error(err.message ?? "Failed to delete category");
    } finally {
      setBusy(false);
    }
  };

  if (isLoading) {
    return <div className="p-6 text-sm text-muted-foreground">Loading…</div>;
  }
  if (!isSuperadmin) {
    return <div className="p-6 text-sm text-muted-foreground">Superadmin access required.</div>;
  }

  return (
    <div className="min-h-screen bg-background pb-24">
      <div className="px-6 pt-8 pb-6">
        <SettingsBackLink to="/platform/data" label="Data Management" />
        <div className="flex items-center gap-2 mb-1">
          <Boxes className="w-5 h-5 text-primary" />
          <h1 className="text-2xl font-bold text-foreground">Appliance Category Management</h1>
          <Badge variant="secondary">{categories.length}</Badge>
        </div>
        <p className="text-muted-foreground">
          Global master list of appliance categories. These flow downstream to Company Settings,
          where each company picks from this list.
        </p>
      </div>

      <div className="px-6">
        <div className="bg-card rounded-xl border border-border p-4 mb-4">
          <label className="block text-sm font-medium text-foreground mb-2">
            Appliance Category Name
          </label>
          <div className="flex gap-2">
            <Input
              value={newCategoryName}
              onChange={(e) => setNewCategoryName(e.target.value)}
              placeholder="e.g. Washing Machines, Refrigerators"
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  handleCreateCategory();
                }
              }}
            />
            <Button onClick={handleCreateCategory} disabled={busy || !newCategoryName.trim()}>
              <Plus className="w-4 h-4 mr-1" /> Add Category
            </Button>
          </div>
        </div>

        <div className="bg-card rounded-xl border border-border overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead className="w-[170px]">Created</TableHead>
                <TableHead className="w-[120px] text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {categories.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={3} className="text-center text-sm text-muted-foreground py-6">
                    No categories yet.
                  </TableCell>
                </TableRow>
              ) : (
                categories.map((c) => (
                  <TableRow key={c.id}>
                    <TableCell className="font-medium">{c.name}</TableCell>
                    <TableCell className="text-xs text-muted-foreground">
                      {new Date(c.created_at).toLocaleDateString()}
                    </TableCell>
                    <TableCell className="text-right">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => {
                          setEditCategory(c);
                          setEditCategoryName(c.name);
                        }}
                        aria-label={`Edit ${c.name}`}
                      >
                        <Pencil className="w-4 h-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="text-destructive hover:text-destructive"
                        onClick={() => setDeleteCategory(c)}
                        aria-label={`Delete ${c.name}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>

        <div className="mt-6">
          <SparePartBaseLogicSection categories={categories} />
        </div>
      </div>

      <Dialog open={!!editCategory} onOpenChange={(o) => !o && setEditCategory(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit appliance category</DialogTitle>
          </DialogHeader>
          <Input
            value={editCategoryName}
            onChange={(e) => setEditCategoryName(e.target.value)}
            placeholder="Category name"
            autoFocus
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditCategory(null)}>Cancel</Button>
            <Button onClick={handleUpdateCategory} disabled={busy || !editCategoryName.trim()}>
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!deleteCategory} onOpenChange={(o) => !o && setDeleteCategory(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="text-destructive">
              Delete "{deleteCategory?.name}"?
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            This removes the category from the global master list. Companies that have selected it
            will no longer see it as an available option.
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteCategory(null)}>Cancel</Button>
            <Button variant="destructive" onClick={handleDeleteCategory} disabled={busy}>
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
